import { useState } from "react";
import { Copy, Check, ExternalLink, Code, Palette, Store } from "lucide-react";
import { toast } from "sonner";

const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const origin = backendUrl.replace(/\/$/, "");

const SNIPPET_ONE_LINER = `<div id="cd-paddle-finder"></div>
<script src="${origin}/embed.js" async></script>`;

const SNIPPET_THEMED = `<div id="cd-paddle-finder"></div>
<script src="${origin}/embed.js"
        data-primary="#0B6E4F"
        data-accent="#FFB400"
        data-ink="#0F1A16"
        data-surface="#F7F9F8"
        data-font="Inter, system-ui, sans-serif"
        data-radius-card="14px"
        data-radius-button="10px"
        data-shop-domain="clubdinksupply.com"
        data-min-height="820"
        async></script>`;

const SNIPPET_LIQUID = `{% comment %} sections/paddle-finder.liquid {% endcomment %}
<div id="cd-paddle-finder"></div>
<script src="${origin}/embed.js"
        data-primary="{{ settings.colors_accent_1 | default: '#0B6E4F' }}"
        data-accent="{{ settings.colors_accent_2 | default: '#FFB400' }}"
        data-font="{{ settings.type_body_font.family }}, {{ settings.type_body_font.fallback_families }}"
        data-shop-domain="{{ shop.permanent_domain }}"
        async></script>

{% schema %}
{
  "name": "Paddle Finder",
  "settings": [],
  "presets": [{ "name": "Paddle Finder" }]
}
{% endschema %}`;

const CodeBlock = ({ code, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  return (
    <div style={{ position: "relative" }}>
      <pre
        style={{
          background: "var(--cd-ink)",
          color: "#E7F1EC",
          padding: "18px 60px 18px 18px",
          borderRadius: 12,
          fontSize: 12.5,
          fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace",
          lineHeight: 1.6,
          overflowX: "auto",
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
        data-testid={testId}
      >
        {code}
      </pre>
      <button
        onClick={copy}
        className="cd-btn-secondary"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          padding: "6px 10px",
          minHeight: 32,
          fontSize: 12,
          background: "#fff",
        }}
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const Step = ({ n, title, children }) => (
  <div style={{ display: "flex", gap: 14, marginBottom: 20 }}>
    <div
      style={{
        width: 30,
        height: 30,
        borderRadius: 999,
        background: "var(--cd-primary)",
        color: "#fff",
        display: "grid",
        placeItems: "center",
        fontWeight: 700,
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {n}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{title}</div>
      <div className="cd-sub" style={{ marginBottom: 10 }}>{children}</div>
    </div>
  </div>
);

const Section = ({ label, title, children, icon: Icon }) => (
  <div className="cd-card" style={{ padding: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--cd-muted)" }}>
      {Icon && <Icon size={13} />}
      <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
        {label}
      </div>
    </div>
    {title && <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>{title}</div>}
    {children}
  </div>
);

export default function Install() {
  return (
    <div className="cd-container-wide cd-court-bg cd-fade-in" data-testid="install-page">
      <span className="cd-chip cd-chip-accent" style={{ marginBottom: 12 }}>
        <Code size={12} /> Shopify install
      </span>
      <h1 className="cd-h1" style={{ margin: "10px 0 6px" }}>
        Add the Paddle Finder to your Shopify store.
      </h1>
      <p className="cd-sub" style={{ marginBottom: 32, maxWidth: 640 }}>
        One line of code. Auto-resizing iframe. Inherits your Shopify theme colors and fonts.
        Add-to-cart uses secure Shopify permalinks — no app to install, no API keys.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, maxWidth: 820 }}>
        {/* THE ONE-LINER */}
        <Section label="The one-liner" icon={Code}>
          <CodeBlock code={SNIPPET_ONE_LINER} testId="snippet-one-liner" />
          <div className="cd-micro" style={{ marginTop: 10 }}>
            Uses default Club Dink brand tokens. Paste anywhere you want the finder to appear.
          </div>
        </Section>

        {/* THEMED */}
        <Section label="With theme colors & fonts" icon={Palette}>
          <CodeBlock code={SNIPPET_THEMED} testId="snippet-themed" />
          <div className="cd-micro" style={{ marginTop: 10 }}>
            Override any brand token via <code>data-*</code> attributes. Change the primary, accent,
            font family, corner radii, or the shop domain used for cart links.
          </div>
        </Section>

        {/* LIQUID SECTION */}
        <Section label="Auto-sync with Shopify theme settings" icon={Store}>
          <CodeBlock code={SNIPPET_LIQUID} testId="snippet-liquid" />
          <div className="cd-micro" style={{ marginTop: 10 }}>
            Save as <code>sections/paddle-finder.liquid</code> — the finder will pick up your theme&apos;s
            accent colors and body font automatically from the theme editor.
          </div>
        </Section>

        {/* HOW TO INSTALL */}
        <Section label="How to install on clubdinksupply.com" icon={Store}>
          <Step n={1} title="Log into your Shopify admin">
            Go to <b>Online Store → Pages → Add page</b>. Name it &quot;Find Your Paddle&quot; (URL:
            <code> /pages/find-your-paddle</code>).
          </Step>
          <Step n={2} title="Open the source-code view">
            In the page content editor, click the <b>Show HTML</b> icon (
            <code>&lt;/&gt;</code>) in the toolbar.
          </Step>
          <Step n={3} title="Paste one of the snippets above">
            Use the themed snippet to match your Club Dink look, or the one-liner for defaults.
            Click <b>Save</b>.
          </Step>
          <Step n={4} title="Add it to your main navigation">
            Go to <b>Online Store → Navigation → Main menu</b>, click <b>Add menu item</b>,
            name it &quot;Paddle Finder&quot;, and link to the page you just created.
          </Step>
          <Step n={5} title="(Optional) Embed as a homepage section">
            Save the Liquid snippet as <code>sections/paddle-finder.liquid</code> via
            <b> Online Store → Themes → ⋯ → Edit code</b>. Then in the theme editor, click
            <b> Add section → Paddle Finder</b> on any template.
          </Step>
          <Step n={6} title="Publish and test">
            Preview the page. Take the quiz, tap Add to cart — you should land in the real
            clubdinksupply.com cart with the paddle already inside.
          </Step>
        </Section>

        {/* THEME OPTIONS TABLE */}
        <Section label="All theme options" icon={Palette}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--cd-muted)", borderBottom: "1px solid var(--cd-border)" }}>
                  <th style={{ padding: "8px 10px", fontWeight: 600 }}>Attribute</th>
                  <th style={{ padding: "8px 10px", fontWeight: 600 }}>Purpose</th>
                  <th style={{ padding: "8px 10px", fontWeight: 600 }}>Default</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["data-primary", "Buttons, badges, focus rings", "#0B6E4F"],
                  ["data-accent", "Progress fill, highlights, top-match", "#FFB400"],
                  ["data-ink", "Headings and body text", "#0F1A16"],
                  ["data-surface", "Card and quiz-option background", "#F7F9F8"],
                  ["data-bg", "Page background inside the widget", "#FFFFFF"],
                  ["data-font", "Font family stack for the widget", "Inter, system-ui"],
                  ["data-radius-card", "Card corner radius", "14px"],
                  ["data-radius-button", "Button corner radius", "10px"],
                  ["data-shop-domain", "Shopify domain used for /cart permalinks", "clubdinksupply.com"],
                  ["data-min-height", "Iframe minimum height in px", "720"],
                  ["data-target", "CSS selector for mount element", "#cd-paddle-finder"],
                ].map(([attr, purpose, def]) => (
                  <tr key={attr} style={{ borderBottom: "1px solid var(--cd-border)" }}>
                    <td style={{ padding: "10px", fontFamily: "ui-monospace, monospace", color: "var(--cd-primary)" }}>{attr}</td>
                    <td style={{ padding: "10px" }}>{purpose}</td>
                    <td style={{ padding: "10px", fontFamily: "ui-monospace, monospace", color: "var(--cd-muted)" }}>{def}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a
            href={`${origin}/?embed=1&primary=%230B6E4F&accent=%23FFB400`}
            target="_blank"
            rel="noreferrer"
            className="cd-btn-secondary"
            style={{ textDecoration: "none" }}
            data-testid="install-preview-btn"
          >
            <ExternalLink size={14} /> Preview embed
          </a>
          <a
            href={`${origin}/embed.js`}
            target="_blank"
            rel="noreferrer"
            className="cd-btn-secondary"
            style={{ textDecoration: "none" }}
            data-testid="install-script-btn"
          >
            <Code size={14} /> View embed.js
          </a>
          <a
            href={`${origin}/embed-test.html`}
            target="_blank"
            rel="noreferrer"
            className="cd-btn-secondary"
            style={{ textDecoration: "none" }}
            data-testid="install-demo-btn"
          >
            <ExternalLink size={14} /> Live demo host page
          </a>
        </div>
      </div>
    </div>
  );
}
